import React, { useState, useEffect } from "react";
import {
  Form,
  Input,
  Button,
  Select,
  Upload,
  Typography,
  Row,
  Col,
  message as antdMessage,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { PrintingService } from "../../config/api";
import PaymentOptions from "./paymentOptions";
import axios from "axios";
import JSZip from "jszip";
import './docPrinting.css';

const { Title } = Typography;
const { Option } = Select;

const printOptions = [
  { label: `Color`, value: `Color`, price: 10, disabled: false },
  { label: `Black & White`, value: `Black & White`, price: 5, disabled: false },
  { label: `Color Photo (A4–180 GSM)`, value: `Color Photo (A4–180 GSM)`, price: 25, disabled: false },
  { label: `Color Photo (Postcard Size–4"×6")`, value: `Color Photo (Postcard Size–4"×6")`, price: 16, disabled: false },
  { label: `🐰Sticker/Label (A4–Sticker Paper)`, value: `Sticker/Label (A4–Sticker Paper)`, price: 25, disabled: false },
];

const MAX_PAYMENT_PROOFS = 3;
const MAX_DOCS = 15;

const PRINT_BUDDY_NEW_HOME_URL = "https://ziniz.online/PrintBuddy";

const PrintingRequest = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [documentFiles, setDocumentFiles] = useState([]);
  const [paymentFiles, setPaymentFiles] = useState([]);
  const [docUploadError, setDocUploadError] = useState("");
  const [paymentUploadError, setPaymentUploadError] = useState("");
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const [offlinePeriod, setOfflinePeriod] = useState({ from: "", to: "" });
  const [isOffline, setIsOffline] = useState(false);

  const formatDate = (offlineDateTime) => {
    const date = new Date(offlineDateTime);
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Asia/Kolkata",
    }).format(date);
  };

  // Fetch offline period
  useEffect(() => {
    const fetchOfflinePeriod = async () => {
      try {
        const response = await axios.get(PrintingService);
        setOfflinePeriod(response.data);
      } catch {
        antdMessage.error("You may experience a delay in processing...");
      }
    };
    fetchOfflinePeriod();
  }, []);

  // Check if currently offline
  useEffect(() => {
    if (!(offlinePeriod.from && offlinePeriod.to)) return;
    let timerId;
    const checkOffline = () => {
      const now = new Date().toISOString();
      setIsOffline(now >= offlinePeriod.from && now <= offlinePeriod.to);
    };
    checkOffline();
    timerId = setInterval(checkOffline, 600000);
    return () => clearInterval(timerId);
  }, [offlinePeriod]);

  // Full-page gate on this route: block use of legacy form; only outbound link is actionable
  const blockLegacyPrintPage = !isOffline;

  useEffect(() => {
    if (!blockLegacyPrintPage) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [blockLegacyPrintPage]);

  // ---------- Main Form Submit ----------
  const onFinish = async (values) => {
    setLoading(true);
    try {
      // ---------- ZIP ALL FILES TOGETHER ----------
      const zip = new JSZip();

      // Add documents
      documentFiles.forEach((file) => {
        const actualFile = file.originFileObj || file;
        zip.file("documents/" + actualFile.name, actualFile);
      });

      // Add payment proofs
      paymentFiles.forEach((file) => {
        const actualFile = file.originFileObj || file;
        zip.file("payment_proofs/" + actualFile.name, actualFile);
      });

      // Generate final ZIP blob
      const zipBlob = await zip.generateAsync({ type: "blob" });
      const zipFilename = `PrintRequest_${Date.now()}.zip`;

      // ---------- Request ONE presigned URL ----------
      const presignedResponse = await axios.post(
        PrintingService,
        {
          action: "getPresignedUrls",
          files: [
            {
              filename: zipFilename,
              contentType: "application/zip",
            }
          ],
        },
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      const { url, key } = presignedResponse.data.urls[0];

      // ---------- Upload the ZIP ----------
      await fetch(url, {
        method: "PUT",
        headers: { "Content-Type": "application/zip" },
        body: zipBlob,
      });

      // ---------- Payload to Backend ----------
      const payload = {
        ...values,
        zipFileKey: key,                                     // single zip file
        originalDocumentNames: documentFiles.map((f) => f.name),
        originalPaymentNames: paymentFiles.map((f) => f.name),
      };

      await axios.post(PrintingService, payload);

      antdMessage.success({
        content: "🎉 Thank you! Your Request submitted successfully!",
        duration: 4,
      });

      setSubmissionSuccess(true);
      form.resetFields();
      setDocumentFiles([]);
      setPaymentFiles([]);

    } catch (error) {
      antdMessage.error(
        error.response?.data || error.message || "Submission failed"
      );
    } finally {
      setLoading(false);
    }
  };


  // ---------- Rendering ----------
  return (
    <div className="doc-container">
      {isOffline && (
        <div className="offline-banner">
          <h2>We're currently Offline</h2>
          <br />
          <p>Please visit us again after {formatDate(offlinePeriod.to)}</p>
        </div>
      )}

      {blockLegacyPrintPage && (
        <div
          className="print-new-home-overlay"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="print-new-home-heading"
          aria-describedby="print-new-home-desc"
        >
          <div className="print-new-home-overlay__panel">
            <h2 id="print-new-home-heading" className="print-new-home-overlay__title">
              We've moved
            </h2>
            <p id="print-new-home-desc" className="print-new-home-overlay__text">
              All requests are served from our new official site.
              Please Click on the link below to submit your request.
            </p>
            <a
              href={PRINT_BUDDY_NEW_HOME_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="print-new-home-overlay__cta"
            >
              www.ziniz.online/PrintBuddy
            </a>
            <p className="print-new-home-overlay__note">
              You cannot submit a request from this address anymore.
            </p>
          </div>
        </div>
      )}

      <div
        className="doc-container__main"
        aria-hidden={blockLegacyPrintPage}
        inert={blockLegacyPrintPage ? true : undefined}
      >
      <Title level={3} style={{ textAlign: "center" }}>
        Community Print Buddy <br />
        (Gottigere Lake Road)
      </Title>

      {submissionSuccess ? (
        <div className="success-message">
          <Title level={4}>🎉 Woohoo! Your request is in the queue!</Title>
          <p>
            📬 You’ll get an email confirmation soon.
            <br />
            🖨️ We’ll also ping you when it’s <strong>printed</strong> or if
            anything goes <strong>sideways 🚫</strong>.
          </p>
        </div>
      ) : (
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          autoComplete="on"
          disabled={isOffline || blockLegacyPrintPage}
        >
          {/* Name & Flat */}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Name"
                name="name"
                rules={[{ required: true, message: "Name Please" }]}
              >
                <Input placeholder="Name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Society / Flat No."
                name="flat"
                rules={[{ required: true, message: "Flat No. Please" }]}
              >
                <Input placeholder="e.g. SNG B-2018" />
              </Form.Item>
            </Col>
          </Row>

          {/* Email & Mobile */}
          <Row gutter={16} className="responsive-email-mobile">
            <Col span={15}>
              <Form.Item
                label="Email"
                name="email"
                rules={[
                  { required: true, message: "Email Please" },
                  { type: "email", message: "Enter a valid email" },
                ]}
              >
                <Input placeholder="Email" />
              </Form.Item>
            </Col>
            <Col span={9}>
              <Form.Item
                label="Mobile Number"
                name="mobile"
                rules={[{ required: true, message: "Mobile No. Please" }]}
              >
                <Input placeholder="+91-9876543210" />
              </Form.Item>
            </Col>
          </Row>

          {/* Print Type & Copies */}
          <Row gutter={16} className="responsive-print-copies">
            <Col span={15}>
              <Form.Item
                label="Print Type"
                name="printType"
                rules={[{ required: true, message: "Select a print type" }]}
                initialValue={null}
              >
                <Select placeholder="Select Print Type">
                  {printOptions.map((opt) => (
                    <Option key={opt.value} value={opt.value} disabled={opt.disabled}>
                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <span>{opt.label}</span>
                        <span style={{ color: "#999" }}>
                          {opt.comingSoon ? "Coming Soon" : `₹${opt.price}`}
                        </span>
                      </div>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={9}>
              <Form.Item
                label="Copies"
                name="copies"
                initialValue={1}
                rules={[{ required: true, message: "How many?🤔" }]}
              >
                <Input type="number" min={1} max={200} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Message (optional)" name="message">
            <Input.TextArea rows={3} />
          </Form.Item>

          {/* Document Upload */}
          <Row gutter={16}>
            <Col span={13}>
              <Form.Item
                label="Item to be Printed"
                name="documents"
                rules={[
                  {
                    validator: () => {
                      if (documentFiles.length === 0) return Promise.reject("What shall I print?😕");
                      if (documentFiles.length > MAX_DOCS) {
                        setDocUploadError(`Up to ${MAX_DOCS} items are accepted in a request`);
                        return Promise.reject();
                      }
                      const allowedExtensions = ["pdf", "doc", "docx", "jpg", "jpeg", "png"];
                      const invalidFiles = documentFiles.filter((file) => {
                        const ext = file.name.split(".").pop()?.toLowerCase();
                        return !ext || !allowedExtensions.includes(ext);
                      });
                      if (invalidFiles.length > 0) {
                        setDocUploadError(`Allowed files: ${allowedExtensions.map(e=>'.'+e).join(", ")}`);
                        return Promise.reject();
                      }
                      setDocUploadError("");
                      return Promise.resolve();
                    },
                  },
                ]}
                required
              >
                <Upload
                  multiple
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  beforeUpload={() => false}
                  fileList={documentFiles}
                  onChange={({ fileList: newFileList }) => {
                    if (newFileList.length > MAX_DOCS) {
                      setDocumentFiles(newFileList.slice(0, MAX_DOCS + 1));
                    } else{
                      setDocumentFiles(newFileList);
                    }
                  }}

                  onRemove={(file) => {
                    setDocumentFiles((prev) =>
                      prev.filter((f) => f.uid !== file.uid)
                    );
                    setDocUploadError("");
                  }}
                >
                  <Button icon={<UploadOutlined />}>Select Your Files</Button>
                  {docUploadError && <div className="error-text flash">{docUploadError}</div>}
                </Upload>
              </Form.Item>
            </Col>

            {/* Payment Proof Upload */}
            <Col span={11}>
              <Form.Item label="Payment Proof">
                <Upload
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png"
                  beforeUpload={() => false}
                  fileList={paymentFiles}
                    onChange={({ fileList: newFileList }) => {
                      if (newFileList.length > MAX_PAYMENT_PROOFS) {
                        setPaymentUploadError(
                          `Can upload up to ${MAX_PAYMENT_PROOFS} payment proofs.`
                        );
                        setPaymentFiles(newFileList.slice(0, MAX_PAYMENT_PROOFS));
                      } else {
                        setPaymentUploadError("");
                        setPaymentFiles(newFileList);
                      }
                    }}
                    onRemove={(file) => {
                      setPaymentFiles((prev) =>
                        prev.filter((f) => f.uid !== file.uid)
                      );
                      setPaymentUploadError("");
                    }}
                >
                  <Button icon={<UploadOutlined />}>Select Proof</Button>
                  {paymentUploadError && <div className="error-text flash">{paymentUploadError}</div>}
                </Upload>
              </Form.Item>
            </Col>
          </Row>

          {/* Approx Cost & Payment Options */}
          <Form.Item shouldUpdate noStyle>
            {() => (
              <div style={{ textAlign: "center" }}>
                <ApproximateCost form={form} documentFiles={documentFiles} />
              </div>
            )}
          </Form.Item>
          <br />
          <PaymentOptions />

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading} block disabled={isOffline || blockLegacyPrintPage}>
              Submit Request
            </Button>
          </Form.Item>
        </Form>
      )}
      </div>
    </div>
  );
};

// Approx Cost Component
const ApproximateCost = ({ form, documentFiles }) => {
  const printType = form.getFieldValue("printType");
  const copies = form.getFieldValue("copies") || 1;
  const selectedOption = printOptions.find((opt) => opt.value === printType);
  const rate = selectedOption?.price || 0;
  const numDocs = documentFiles.length || 1;
  const approx = rate * copies * numDocs;
  if (!selectedOption || !rate) return null;
  return (
    <div className="approx-cost-info">
      Approx. Cost: {rate} × {numDocs} file(s) × {copies} copies = <strong>₹{approx}/-</strong>
      <br/>
      (Note: If a file has multiple pages, each page is counted separately)
    </div>
  );
};

export default PrintingRequest;
